import { useState, useEffect } from "react";
import { Mail, Phone, Building2, Clock, Trash2, ChevronDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_OPTIONS = ["new", "contacted", "qualified", "proposal", "closed", "lost"];
const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-yellow-100 text-yellow-700",
  qualified: "bg-purple-100 text-purple-700",
  proposal: "bg-orange-100 text-orange-700",
  closed: "bg-green-100 text-green-700",
  lost: "bg-gray-100 text-gray-500",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchLeads = async () => {
    try {
      const { data } = await axios.get(`${API}/api/leads`);
      setLeads(data);
    } catch { /* empty */ }
    setLoading(false);
  };

  useEffect(() => { fetchLeads(); }, []);

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/api/leads/${id}`, { status });
    fetchLeads();
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await axios.delete(`${API}/api/leads/${id}`);
    fetchLeads();
  };

  const filtered = statusFilter === "all" ? leads : leads.filter(l => l.status === statusFilter);

  return (
    <div data-testid="admin-leads-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="font-serif text-2xl text-navy-900">Lead Management</h1>
        <div className="flex items-center gap-2">
          <span className="text-xs font-sans text-gray-500">Filter:</span>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-9 text-sm font-sans border-gray-200 rounded-sm" data-testid="lead-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Leads</SelectItem>
              {STATUS_OPTIONS.map(s => (
                <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-400 font-sans text-sm">Loading leads...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 border border-gray-200 bg-white">
          <p className="text-gray-400 font-sans">No leads found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((lead) => (
            <div key={lead.id} data-testid={`lead-item-${lead.id}`} className="bg-white border border-gray-200 p-5">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-sans text-sm font-medium text-navy-900">{lead.name}</h3>
                    <span className={`text-xs font-sans px-2 py-0.5 rounded-sm capitalize ${STATUS_COLORS[lead.status] || STATUS_COLORS.new}`}>
                      {lead.status}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-xs text-gray-400 font-sans">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {lead.email}</span>
                    {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {lead.phone}</span>}
                    {lead.company && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {lead.company}</span>}
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(lead.created_at).toLocaleDateString()}</span>
                  </div>
                  {lead.message && (
                    <p className="text-gray-500 font-sans text-sm mt-2 border-l-2 border-gray-200 pl-3">{lead.message}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Select value={lead.status} onValueChange={(v) => updateStatus(lead.id, v)}>
                    <SelectTrigger className="w-32 h-8 text-xs font-sans border-gray-200 rounded-sm" data-testid={`lead-status-select-${lead.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(s => (
                        <SelectItem key={s} value={s} className="capitalize text-xs">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    onClick={() => deleteLead(lead.id)}
                    data-testid={`lead-delete-${lead.id}`}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
